'use client';
import React, { useState } from 'react';
import { Check, Plus, Minus, ArrowRight, Droplets, Sparkles, Shield, Star, Wrench, Lightbulb, Settings, Wind, Car } from 'lucide-react';
import type { SelectedService } from './BookingWizard';

// ─── Detailing Packages ────────────────────────────────────────────────────
interface PricingTier {
  label: string;
  price: string;
  numericPrice: number; // low-end numeric for calculations
}

interface ServicePackage {
  id: string;
  icon: React.ElementType;
  name: string;
  tagline: string;
  features: string[];
  pricing: PricingTier[];
  pricingNote?: string;
  category: string;
  popular: boolean;
  durationHours: number;
}

const SERVICES: ServicePackage[] = [
  {
    id: 'svc-precision-exterior',
    icon: Droplets,
    name: 'Precision Exterior Wash',
    tagline: 'A thorough exterior clean from top to bottom',
    popular: false,
    durationHours: 1,
    features: [
      'High Pressure Wash',
      'Full Hand Wash',
      'Tire and Wheel Detox',
      'Tire Shine',
    ],
    pricing: [
      { label: 'Coupe & Sedan', price: '$50', numericPrice: 50 },
      { label: 'Small SUV/Truck', price: '$75', numericPrice: 75 },
      { label: 'Large SUV/Truck', price: '$100', numericPrice: 100 },
    ],
    category: 'Exterior',
  },
  {
    id: 'svc-essentials-package',
    icon: Sparkles,
    name: 'Essentials Package',
    tagline: 'Interior & exterior care with lasting protection',
    popular: true,
    durationHours: 4,
    features: [
      'Full Hand Wash',
      'Wheel & Tire Detox',
      'Full Interior Vacuum',
      'Surface Wipe Down & UV Protection',
      '1–2 Month Paint Sealant',
      'Tire Shine',
    ],
    pricing: [
      { label: 'Coupe & Sedan', price: '$150', numericPrice: 150 },
      { label: 'Small SUV/Truck', price: '$175', numericPrice: 175 },
      { label: 'Large SUV/Truck', price: '$200', numericPrice: 200 },
    ],
    category: 'Full Detail',
  },
  {
    id: 'svc-precision-package',
    icon: Shield,
    name: 'Precision Package',
    tagline: 'Deep clean and decontamination for a like-new finish',
    popular: false,
    durationHours: 5,
    features: [
      'Everything in Essentials Package',
      'Carpet & Seat Shampoo/Extraction',
      'Steam Sanitation',
      'Clay Bar Treatment',
      'Iron Decontamination',
      '3–6 Month Paint Sealant',
    ],
    pricing: [
      { label: 'Coupe & Sedan', price: '$275', numericPrice: 275 },
      { label: 'Small SUV/Truck', price: '$325', numericPrice: 325 },
      { label: 'Large SUV/Truck', price: '$375', numericPrice: 375 },
    ],
    category: 'Premium Detail',
  },
  {
    id: 'svc-surface-refinement',
    icon: Star,
    name: 'Surface Refinement Package',
    tagline: 'Full restoration with multi-stage paint correction',
    popular: false,
    durationHours: 8,
    features: [
      'Everything from Precision Package',
      'Full Exterior/Interior Restoration',
      'Multi-stage Paint Correction',
      'Headlight Restoration (if needed)',
      '6–12 Month Paint Protection',
    ],
    pricing: [
      { label: 'Coupe & Sedan', price: 'Starting $600', numericPrice: 600 },
      { label: 'Small SUV/Truck', price: 'Starting $750', numericPrice: 750 },
      { label: 'Large SUV/Truck', price: 'Starting $900', numericPrice: 900 },
    ],
    pricingNote: 'Price depends on paint condition',
    category: 'Restoration',
  },
  {
    id: 'svc-surface-correction',
    icon: Wrench,
    name: 'Surface Correction Package',
    tagline: 'Restores clarity, depth, and gloss to your paint',
    popular: false,
    durationHours: 8,
    features: [
      'Full Exterior Wash',
      'Chemical Decontamination',
      'Machine Paint Correction',
      'Restores clarity, depth, and gloss',
    ],
    pricing: [
      { label: 'Coupe & Sedan', price: '$600–$800', numericPrice: 600 },
      { label: 'Small SUV/Truck', price: '$750–$950', numericPrice: 750 },
      { label: 'Large SUV/Truck', price: '$900–$1,200', numericPrice: 900 },
    ],
    pricingNote: 'Pricing depends on paint condition',
    category: 'Paint Correction',
  },
];

// ─── Ceramic Coating Tiers ─────────────────────────────────────────────────
interface CoatingTier {
  id: string;
  name: string;
  duration: string;
  popular: boolean;
  pricing: PricingTier[];
  durationHours: number;
}

const COATING_TIERS: CoatingTier[] = [
  {
    id: 'coating-1yr',
    name: '1 Year Coating',
    duration: 'Entry Level',
    popular: false,
    durationHours: 12,
    pricing: [
      { label: 'Coupe & Sedan', price: '$700–$900', numericPrice: 700 },
      { label: 'Small SUV/Truck', price: '$900–$1,100', numericPrice: 900 },
      { label: 'Large SUV/Truck', price: '$1,100–$1,300', numericPrice: 1100 },
    ],
  },
  {
    id: 'coating-3yr',
    name: '3 Year Coating',
    duration: 'Most Popular',
    popular: true,
    durationHours: 12,
    pricing: [
      { label: 'Coupe & Sedan', price: '$1,000–$1,300', numericPrice: 1000 },
      { label: 'Small SUV/Truck', price: '$1,300–$1,600', numericPrice: 1300 },
      { label: 'Large SUV/Truck', price: '$1,500–$1,900', numericPrice: 1500 },
    ],
  },
  {
    id: 'coating-5yr',
    name: '5 Year Coating',
    duration: 'Premium',
    popular: false,
    durationHours: 12,
    pricing: [
      { label: 'Coupe & Sedan', price: '$1,400–$1,800', numericPrice: 1400 },
      { label: 'Small SUV/Truck', price: '$1,700–$2,200', numericPrice: 1700 },
      { label: 'Large SUV/Truck', price: '$2,000–$2,500', numericPrice: 2000 },
    ],
  },
];

// ─── Add-Ons ───────────────────────────────────────────────────────────────
interface AddOnItem {
  id: string;
  icon: React.ElementType;
  name: string;
  pricing: PricingTier[];
}

const ADDONS: AddOnItem[] = [
  {
    id: 'addon-headlight',
    icon: Lightbulb,
    name: 'Headlight Restoration',
    pricing: [
      { label: 'Coupe & Sedan', price: '$100', numericPrice: 100 },
      { label: 'Small SUV/Truck', price: '$125', numericPrice: 125 },
      { label: 'Large SUV/Truck', price: '$150', numericPrice: 150 },
    ],
  },
  {
    id: 'addon-engine-bay',
    icon: Settings,
    name: 'Engine Bay Detail',
    pricing: [
      { label: 'All Vehicles', price: '$50', numericPrice: 50 },
    ],
  },
  {
    id: 'addon-odor',
    icon: Wind,
    name: 'Interior Odor Treatment',
    pricing: [
      { label: 'Coupe & Sedan', price: '$75', numericPrice: 75 },
      { label: 'Small SUV/Truck', price: '$85', numericPrice: 85 },
      { label: 'Large SUV/Truck', price: '$95', numericPrice: 95 },
    ],
  },
];

// ─── Ceramic Coating Add-Ons ───────────────────────────────────────────────
const COATING_ADDONS: AddOnItem[] = [
  {
    id: 'coating-addon-wheels',
    icon: Settings,
    name: 'Wheel Coating',
    pricing: [
      { label: 'Coupe & Sedan', price: '$150', numericPrice: 150 },
      { label: 'Small SUV/Truck', price: '$200', numericPrice: 200 },
      { label: 'Large SUV/Truck', price: '$250', numericPrice: 250 },
    ],
  },
  {
    id: 'coating-addon-glass',
    icon: Sparkles,
    name: 'Glass Coating',
    pricing: [
      { label: 'Coupe & Sedan', price: '$100', numericPrice: 100 },
      { label: 'Small SUV/Truck', price: '$150', numericPrice: 150 },
      { label: 'Large SUV/Truck', price: '$200', numericPrice: 200 },
    ],
  },
  {
    id: 'coating-addon-trim',
    icon: Wrench,
    name: 'Trim Coating',
    pricing: [
      { label: 'Coupe & Sedan', price: '$100', numericPrice: 100 },
      { label: 'Small SUV/Truck', price: '$150', numericPrice: 150 },
      { label: 'Large SUV/Truck', price: '$200', numericPrice: 200 },
    ],
  },
  {
    id: 'coating-addon-leather',
    icon: Shield,
    name: 'Leather Seat Protection',
    pricing: [
      { label: 'Coupe & Sedan', price: '$150', numericPrice: 150 },
      { label: 'Small SUV/Truck', price: '$200', numericPrice: 200 },
      { label: 'Large SUV/Truck', price: '$250', numericPrice: 250 },
    ],
  },
];

// ─── Vehicle Size Options ──────────────────────────────────────────────────
const VEHICLE_SIZES = ['Coupe & Sedan', 'Small SUV/Truck', 'Large SUV/Truck'];

// Helper: get the pricing tier that matches the selected vehicle size
function getPricingForVehicle(pricing: PricingTier[], vehicleSize: string): PricingTier {
  return pricing.find((p) => p.label === vehicleSize) ?? pricing[pricing.length - 1];
}

interface Props {
  onSelect: (service: SelectedService) => void;
  selectedId: string | null;
}

export default function StepServiceSelection({ onSelect, selectedId }: Props) {
  const [chosenId, setChosenId] = useState<string | null>(selectedId);
  const [selectedVehicleSize, setSelectedVehicleSize] = useState<string>('Coupe & Sedan');
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);

  const toggleAddon = (id: string) => {
    setSelectedAddons((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const isServiceChosen = SERVICES.some((s) => s.id === chosenId);
  const isCoatingChosen = COATING_TIERS.some((c) => c.id === chosenId);
  const showVehicleSelector = isServiceChosen || isCoatingChosen;

  // Determine which add-ons to show
  const activeAddons = isCoatingChosen ? COATING_ADDONS : ADDONS;

  const handleContinue = () => {
    const svc = SERVICES.find((s) => s.id === chosenId);
    const coating = COATING_TIERS.find((c) => c.id === chosenId);

    const addonItems = activeAddons
      .filter((a) => selectedAddons.includes(a.id))
      .map((a) => {
        const tier = getPricingForVehicle(a.pricing, selectedVehicleSize);
        return {
          id: a.id,
          name: a.name,
          price: tier.numericPrice,
          priceLabel: tier.price,
        };
      });

    if (svc) {
      const tier = getPricingForVehicle(svc.pricing, selectedVehicleSize);
      onSelect({
        id: svc.id,
        name: svc.name,
        price: tier.numericPrice,
        priceLabel: tier.price,
        vehicleSize: selectedVehicleSize,
        duration: svc.durationHours,
        addons: addonItems,
      });
    } else if (coating) {
      const tier = getPricingForVehicle(coating.pricing, selectedVehicleSize);
      onSelect({
        id: coating.id,
        name: coating.name,
        price: tier.numericPrice,
        priceLabel: tier.price,
        vehicleSize: selectedVehicleSize,
        duration: coating.durationHours,
        addons: addonItems,
      });
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-foreground mb-2">Choose Your Service</h1>
        <p className="text-muted-foreground">Select the detailing package that fits your vehicle and budget.</p>
      </div>

      {/* ── Detailing Packages ── */}
      <div className="mb-10">
        <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-3">Detailing Packages</p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {SERVICES.map((service) => {
            const ServiceIcon = service.icon;
            const isChosen = chosenId === service.id;

            return (
              <button
                key={service.id}
                onClick={() => { setChosenId(service.id); setSelectedAddons([]); }}
                className={`relative text-left bg-card rounded-xl p-5 border transition-all duration-200 flex flex-col ${
                  isChosen
                    ? 'border-primary shadow-gold bg-primary/5'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                {service.popular && (
                  <div className="absolute -top-2.5 left-4">
                    <span className="px-2.5 py-0.5 rounded-full gold-gradient text-primary-foreground text-xs font-bold">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Check indicator */}
                <div className={`absolute top-4 right-4 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                  isChosen ? 'border-primary bg-primary' : 'border-border'
                }`}>
                  {isChosen && <Check size={11} className="text-primary-foreground" />}
                </div>

                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                    isChosen ? 'bg-primary/20' : 'bg-muted/60'
                  }`}>
                    <ServiceIcon size={18} className={isChosen ? 'text-primary' : 'text-muted-foreground'} />
                  </div>
                  <div className="pr-6">
                    <p className="text-sm font-bold text-foreground">{service.name}</p>
                    <p className="text-xs text-muted-foreground">{service.tagline}</p>
                  </div>
                </div>

                <ul className="space-y-1 mb-3 flex-1">
                  {service.features.map((feat) => (
                    <li key={feat} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>

                <div className="pt-3 border-t border-border mt-auto">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Pricing</p>
                  <ul className="space-y-1 mb-1">
                    {service.pricing.map((tier) => (
                      <li key={tier.label} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{tier.label}</span>
                        <span className="font-bold text-foreground">{tier.price}</span>
                      </li>
                    ))}
                  </ul>
                  {service.pricingNote && (
                    <p className="text-xs text-muted-foreground italic mt-1">{service.pricingNote}</p>
                  )}
                  <span className="inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground">
                    {service.category}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Ceramic Coating Packages ── */}
      <div className="mb-10">
        <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-1">Precision Ceramic Coating</p>
        <p className="text-sm text-muted-foreground mb-4">Long-term hydrophobic protection with professional-grade ceramic coating.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {COATING_TIERS.map((tier) => {
            const isChosen = chosenId === tier.id;

            return (
              <button
                key={tier.id}
                onClick={() => { setChosenId(tier.id); setSelectedAddons([]); }}
                className={`relative text-left bg-card rounded-xl p-5 border transition-all duration-200 flex flex-col ${
                  isChosen
                    ? 'border-primary shadow-gold bg-primary/5'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-2.5 left-4">
                    <span className="px-2.5 py-0.5 rounded-full gold-gradient text-primary-foreground text-xs font-bold">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Check indicator */}
                <div className={`absolute top-4 right-4 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                  isChosen ? 'border-primary bg-primary' : 'border-border'
                }`}>
                  {isChosen && <Check size={11} className="text-primary-foreground" />}
                </div>

                <div className="flex items-start gap-3 mb-3 pr-6">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                    isChosen ? 'bg-primary/20' : 'bg-muted/60'
                  }`}>
                    <Shield size={18} className={isChosen ? 'text-primary' : 'text-muted-foreground'} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">{tier.name}</p>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border inline-block mt-0.5">
                      {tier.duration}
                    </span>
                  </div>
                </div>

                <div className="pt-3 border-t border-border mt-auto">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Pricing by Vehicle</p>
                  <ul className="space-y-1">
                    {tier.pricing.map((p) => (
                      <li key={p.label} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{p.label}</span>
                        <span className="font-bold text-foreground">{p.price}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Vehicle Size Selector (shown when any service/coating is chosen) ── */}
      {showVehicleSelector && (
        <div className="mb-8 bg-card border border-border rounded-xl p-5 animate-slide-up">
          <div className="flex items-center gap-2 mb-3">
            <Car size={16} className="text-primary" />
            <h3 className="text-sm font-bold text-foreground">Select Your Vehicle Size</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">This determines your exact pricing for the selected service and any add-ons.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {VEHICLE_SIZES.map((size) => {
              const isSelected = selectedVehicleSize === size;
              // Get the price for this vehicle size for the chosen service/coating
              const svc = SERVICES.find((s) => s.id === chosenId);
              const coating = COATING_TIERS.find((c) => c.id === chosenId);
              const pricingSource = svc?.pricing ?? coating?.pricing ?? [];
              const priceTier = getPricingForVehicle(pricingSource, size);

              return (
                <button
                  key={size}
                  onClick={() => setSelectedVehicleSize(size)}
                  className={`flex flex-col items-start p-4 rounded-xl border text-left transition-all duration-200 ${
                    isSelected
                      ? 'border-primary bg-primary/5 shadow-gold'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  <div className="flex items-center justify-between w-full mb-1">
                    <span className="text-sm font-semibold text-foreground">{size}</span>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                      isSelected ? 'border-primary bg-primary' : 'border-border'
                    }`}>
                      {isSelected && <Check size={9} className="text-primary-foreground" />}
                    </div>
                  </div>
                  <span className={`text-base font-extrabold tabular-nums ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                    {priceTier.price}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Add-Ons (shown when a service/coating is selected) ── */}
      {showVehicleSelector && (
        <div className="mb-8 bg-card border border-border rounded-xl p-5 animate-slide-up">
          <h3 className="text-sm font-bold text-foreground mb-1">Optional Add-Ons</h3>
          <p className="text-xs text-muted-foreground mb-4">
            Enhance your service with these extras — priced for your <span className="text-foreground font-medium">{selectedVehicleSize}</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {activeAddons.map((addon) => {
              const AddonIcon = addon.icon;
              const isSelected = selectedAddons.includes(addon.id);
              const priceTier = getPricingForVehicle(addon.pricing, selectedVehicleSize);
              return (
                <button
                  key={addon.id}
                  onClick={() => toggleAddon(addon.id)}
                  className={`flex flex-col p-3 rounded-lg border text-left transition-all duration-200 ${
                    isSelected
                      ? 'border-primary/50 bg-primary/5 text-foreground'
                      : 'border-border text-muted-foreground hover:border-border/80 hover:bg-muted/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded flex items-center justify-center border transition-all ${
                        isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                      }`}>
                        {isSelected ? <Minus size={10} className="text-primary-foreground" /> : <Plus size={10} />}
                      </div>
                      <AddonIcon size={14} className={isSelected ? 'text-primary' : 'text-muted-foreground'} />
                    </div>
                    <span className={`text-sm font-extrabold tabular-nums ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                      {priceTier.price}
                    </span>
                  </div>
                  <span className="text-xs font-medium pl-6">{addon.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Continue button */}
      <button
        onClick={handleContinue}
        disabled={!chosenId}
        className={`w-full md:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-bold transition-all duration-150 active:scale-95 ${
          chosenId
            ? 'gold-gradient text-primary-foreground hover:opacity-90 shadow-gold'
            : 'bg-muted text-muted-foreground cursor-not-allowed'
        }`}
      >
        Continue to Date & Time
        <ArrowRight size={18} />
      </button>
    </div>
  );
}